import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Link as LinkIcon, Check, Trash2, UserX, ThumbsUp, ThumbsDown, Edit3, Zap, MessageCircle, Bookmark, Flag, MoreHorizontal } from 'lucide-react'
import { useState, useRef, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { useAdmin } from '../../contexts/AdminContext'
import { deleteEvent, reactToEvent } from '../../lib/nostr'
import DOMPurify from 'dompurify'
import { formatDistanceToNow } from 'date-fns'
import { tr } from 'date-fns/locale'
import { generateEntryUrl, generateSlug, generateTopicUrl } from '../../lib/urlUtils'
import { parseDictionaryContent, extractImages } from '../../lib/parser'
import { useNostrProfile } from '../../hooks/useNostrProfile'
import { useReplyCount } from '../../hooks/useReplies'
import { useSingleBookmark } from '../../hooks/useBookmarks'
import { EditEntryModal } from './EditEntryModal'
import { ZapModal } from '../Zap/ZapModal'
import { ReplyModal } from '../Reply/ReplyModal'
import { ReactionPicker } from './ReactionPicker'
import { ReportModal } from '../Modals/ReportModal'
import { UserAvatar } from '../Common/UserAvatar'
import { Lightbox } from '../Common/Lightbox'
import { useToast } from '../../contexts/ToastContext'
import { useReactions } from '../../hooks/useReactions'
import { Event, nip19 } from 'nostr-tools'

interface EntryCardProps {
  entry: {
    id: string
    content: string
    created_at: string
    author_username: string
    author_pubkey?: string
    author_avatar_url: string | null
    author_reputation: number
    upvotes: number
    downvotes: number
    topicTitle?: string
    userVotes?: Record<string, { content: string, id: string }>
    isExternal?: boolean
    rawEvent?: Event
  }
  showPermalink?: boolean
  hideTopicHeader?: boolean
  fetchReactions?: boolean
}

export function EntryCard({ entry, hideTopicHeader = false, fetchReactions = false }: EntryCardProps) {
  const { isAdmin, sk, pubkey: currentUserPubkey } = useAuth()
  const { muteUser } = useAdmin()
  const [isDeleting, setIsDeleting] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [isZapModalOpen, setIsZapModalOpen] = useState(false)
  const [isReplyModalOpen, setIsReplyModalOpen] = useState(false)
  const [isReportModalOpen, setIsReportModalOpen] = useState(false)
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  const [lightboxState, setLightboxState] = useState<{ src: string, linkTo?: string, linkState?: any, info?: any } | null>(null)

  const menuRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)

  const { metadata, isLoading } = useNostrProfile(entry.author_pubkey || null)
  const { count: replyCount } = useReplyCount(entry.id)
  const { isBookmarked, toggleBookmark } = useSingleBookmark(entry.id)

  const { upvotes: liveUp, downvotes: liveDown, userVotes: liveUserVotes } = useReactions(fetchReactions ? entry.id : null)

  const finalUpvotes = fetchReactions ? liveUp : (entry.upvotes || 0)
  const finalDownvotes = fetchReactions ? liveDown : (entry.downvotes || 0)
  const finalUserVotes = fetchReactions ? liveUserVotes : (entry.userVotes || {})

  const finalUsername = metadata?.display_name || metadata?.name || entry.author_username
  const finalAvatar = metadata?.picture || entry.author_avatar_url
  const isAuthor = entry.author_pubkey === currentUserPubkey

  let cleanContent = entry.content
  // Strip topic header: "topic;" or "topic\n\n"
  cleanContent = cleanContent.replace(/^[^\n;]+[;\n]+\s*/, '')

  // Clean other suffixes
  cleanContent = cleanContent.replace(/\n\n#turkstr #turkce #turkish #mavililer$/m, '')
  cleanContent = cleanContent.replace(/\n\n#mavililer #mavililersözlük #nostr #türkiye$/m, '')
  cleanContent = cleanContent.replace(/\n---\n📝 Mavililer Sözlük'de paylaşıldı[\s\S]*$/m, '')
  cleanContent = cleanContent.replace(/\n---\n💬 Mavililer Sözlük[\s\S]*$/m, '')

  let parsedContent = parseDictionaryContent(cleanContent)

  // Replace raw image URLs with formatted HTML button "görsel"
  // Matches http/https urls ending with image extensions, with preceding whitespace capture
  const visualRegex = /([\r\n\s]*)(https?:\/\/[a-zA-Z0-9\-\._~:\/?#[\]@!$&'()*+,;=%]+\.(?:jpg|jpeg|png|webp|gif|svg|avif))/gi
  parsedContent = parsedContent.replace(visualRegex, (_match, whitespace, url) => {
    // If original content had newlines, respect them to some degree.
    // If >= 2 newlines (gap), use <br/><br/>. If 1 newline, use <br/>.
    const newlineCount = (whitespace.match(/\n/g) || []).length
    let prefix = ' '
    if (newlineCount >= 2) prefix = '<br/><br/>'
    else if (newlineCount === 1) prefix = '<br/>'

    return `${prefix}<button class="gorsel-trigger inline-flex items-baseline gap-0.5 text-mavi-500 hover:text-mavi-600 dark:text-mavi-400 dark:hover:text-mavi-300 transition-colors font-merriweather text-[0.90rem] hover:underline bg-transparent p-0 border-0 cursor-pointer" data-src="${url}">görsel<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" class="self-center ml-0.5 relative top-[1px]"><rect width="18" height="18" x="3" y="3" rx="2" ry="2"/><circle cx="9" cy="9" r="2"/><path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"/></svg></button>`
  })


  if (entry.isExternal) {
    const hashtags = entry.content.match(/#\w+/g)?.map(t => t.slice(1)) || [];
    hashtags.forEach(tag => {
      const tagRegex = new RegExp(`\\s*#${tag}\\b`, 'gi');
      parsedContent = parsedContent.replace(tagRegex, '');
    });
  }

  const sanitizedContent = DOMPurify.sanitize(parsedContent, {
    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'a', 'span', 'button', 'svg', 'path', 'rect', 'circle'],
    ALLOWED_ATTR: ['href', 'target', 'class', 'title', 'data-spoiler', 'data-src', 'width', 'height', 'viewBox', 'fill', 'stroke', 'stroke-width', 'stroke-linecap', 'stroke-linejoin', 'd', 'x', 'y', 'rx', 'ry', 'cx', 'cy', 'r'],
  })

  // Handle interactions inside the content (Spoilers & Lightbox)
  useEffect(() => {
    const el = contentRef.current
    if (!el) return

    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement

      // Handle Spoiler
      if (target.dataset.spoiler) {
        target.classList.toggle('is-visible')
        return
      }

      // Handle Lightbox (görsel)
      const gorselBtn = target.closest('.gorsel-trigger') as HTMLElement
      if (gorselBtn && gorselBtn.dataset.src) {
        e.preventDefault()
        e.stopPropagation()
        const src = gorselBtn.dataset.src

        // Find index for /img link
        const images = extractImages(entry.content)
        const index = images.findIndex(img => img === src)

        let noteId = entry.id
        try {
          noteId = nip19.noteEncode(entry.id)
        } catch (error) {
          console.error('Failed to encode note ID:', error)
        }

        const info = {
          title: entry.topicTitle || 'Başlıksız',
          author: finalUsername || entry.author_username,
          date: formatDistanceToNow(new Date(entry.created_at), { addSuffix: true, locale: tr })
        }

        setLightboxState({
          src,
          linkTo: index !== -1 ? `/img/${noteId}/${index}` : undefined,
          linkState: { event: entry.rawEvent },
          info
        })
      }
    }

    el.addEventListener('click', handleClick)
    return () => el.removeEventListener('click', handleClick)
  }, [sanitizedContent])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMenuOpen(false)
      }
    }
    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isMenuOpen])

  const { showToast } = useToast()
  const [optimisticVote, setOptimisticVote] = useState<{ type: '+' | '-' | null, isDeleting: boolean } | null>(null)

  const existingVote = currentUserPubkey ? finalUserVotes[currentUserPubkey] : null
  const currentVoteType = optimisticVote ? optimisticVote.type : (existingVote?.content === '+' ? '+' : existingVote?.content === '-' ? '-' : null)

  const handleVote = async (type: '+' | '-') => {
    if (!sk && !window.nostr) {
      showToast('Oy vermek için giriş yapmalısınız.', 'info')
      return
    }
    if (!currentUserPubkey) return;
    if (currentVoteType === type) {
      if (existingVote?.id) {
        setOptimisticVote({ type: null, isDeleting: true })
        try {
          await deleteEvent(existingVote.id, 'Oyu geri çekme', sk, entry.topicTitle)
        } catch (e) {
          console.error('Remove vote failed:', e)
          setOptimisticVote(null)
        }
      }
      return
    }
    setOptimisticVote({ type, isDeleting: false })
    try {
      if (existingVote?.id) {
        await deleteEvent(existingVote.id, 'Oy değiştirme', sk, entry.topicTitle)
      }
      await reactToEvent(entry.id, entry.author_pubkey || '', type, sk, entry.topicTitle)
    } catch (e) {
      console.error('Vote failed:', e)
      setOptimisticVote(null)
    }
  }

  const handleCopyLink = () => {
    const entryUrl = `${window.location.origin}${generateEntryUrl(entry.id)}`
    navigator.clipboard.writeText(entryUrl).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  const handleDelete = async () => {
    const confirmMessage = isAdmin
      ? 'Bu entry\'yi MAVİLİLER\'den KALICI OLARAK SİLMEK (Güçlü Sil / Global Ban) istediğinize emin misiniz?'
      : 'Bu entry\'yi silmek istediğinize emin misiniz?'
    if (!window.confirm(confirmMessage)) return
    if (!isAuthor && !isAdmin) {
      showToast('Sadece kendi entry\'lerinizi veya admin olarak silebilirsiniz.', 'error')
      return
    }
    setIsDeleting(true)
    try {
      await deleteEvent(entry.id, 'Kullanıcı tarafından silindi', sk, entry.topicTitle)
      if (isAdmin) {
        try {
          const { addToList } = await import('../../services/listService')
          await addToList('moderation', { type: 'event', value: entry.id }, sk, 'moderation_log')
          showToast('Güçlü silme işlemi başarıyla tamamlandı.', 'success')
        } catch (listErr) {
          console.error('🛡️ Exception while adding to moderation list:', listErr)
        }
      } else {
        const topicSlug = entry.topicTitle ? generateSlug(entry.topicTitle) : ''
        window.dispatchEvent(new CustomEvent('entry-deleted', {
          detail: { entryId: entry.id, topicSlug }
        }))
        showToast('Entry silindi.', 'success')
      }
    } catch (err) {
      console.error('❌ Delete error:', err)
      showToast(`Silme işlemi başarısız oldu: ${err instanceof Error ? err.message : 'Bilinmeyen hata'}`, 'error')
    } finally {
      setIsDeleting(false)
    }
  }

  const baseUpvotes = finalUpvotes
  const baseDownvotes = finalDownvotes
  let displayUpvotes = baseUpvotes
  let displayDownvotes = baseDownvotes
  if (optimisticVote) {
    if (existingVote?.content === '+') displayUpvotes--
    if (existingVote?.content === '-') displayDownvotes--
    if (optimisticVote.type === '+') displayUpvotes++
    if (optimisticVote.type === '-') displayDownvotes++
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ type: 'spring', damping: 25, stiffness: 200 }}
      className="entry-card group flex flex-col gap-3 relative hover:border-mavi-300/50 dark:hover:border-mavi-700/50 transition-colors"
    >
      <Link
        to={`/entry/${entry.id}`}
        className="absolute inset-0 z-0 rounded-xl"
        aria-label="Entry detayına git"
      />

      {/* Header Area */}
      <div className="flex items-start justify-between gap-4 relative z-10">
        <div className="flex-1">
          {entry.topicTitle && !hideTopicHeader && (
            <div className="flex items-center gap-2">
              <Link
                to={generateTopicUrl(entry.topicTitle, entry.topicTitle)}
                className="text-lg font-poppins font-bold text-gray-900 dark:text-white hover:text-mavi-600 transition-colors inline-block"
                onClick={(e) => e.stopPropagation()}
              >
                {entry.topicTitle.toLowerCase()}
              </Link>
              {entry.isExternal && (
                <span className="px-2 py-0.5 bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 text-[10px] font-poppins font-bold uppercase tracking-wider rounded-full">
                  Dış Kaynak
                </span>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Entry Content */}
      <div
        ref={contentRef}
        className="entry-content prose prose-sm max-w-none dark:prose-invert font-merriweather leading-relaxed relative z-10 text-[0.95rem]"
        dangerouslySetInnerHTML={{ __html: sanitizedContent }}
      />

      {/* Footer Area: Actions and Author */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4 mt-2 border-t border-surface-100 dark:border-surface-800/50 pt-3 relative z-10">
        {/* Interaction Group (Left on Desktop, Corrected Order on Mobile) */}
        <div className="flex flex-row items-center gap-1 w-full sm:w-auto justify-between sm:justify-start" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-1 sm:gap-1">
            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleVote('+'); }}
              className={`flex items-center gap-1.5 px-2 py-1.5 rounded-full transition-all group ${currentVoteType === '+' ? 'text-green-600 font-bold' : 'text-gray-500 hover:text-green-600 hover:bg-green-500/5'}`}
              title="Beğen"
            >
              <ThumbsUp className={`w-4.5 h-4.5 group-hover:scale-110 transition-transform ${currentVoteType === '+' ? 'fill-green-600/20' : ''}`} />
              {displayUpvotes > 0 && <span className="text-sm font-medium">{displayUpvotes}</span>}
            </button>

            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); handleVote('-'); }}
              className={`flex items-center gap-1.5 px-2 py-1.5 rounded-full transition-all group ${currentVoteType === '-' ? 'text-red-600 font-bold' : 'text-gray-500 hover:text-red-600 hover:bg-red-500/5'}`}
              title="Beğenme"
            >
              <ThumbsDown className={`w-4.5 h-4.5 group-hover:scale-110 transition-transform ${currentVoteType === '-' ? 'fill-red-600/20' : ''}`} />
              {displayDownvotes > 0 && <span className="text-sm font-medium">{displayDownvotes}</span>}
            </button>

            <button
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); setIsReplyModalOpen(true); }}
              className="flex items-center gap-1.5 px-2 py-1.5 rounded-full hover:bg-mavi-500/5 text-gray-500 hover:text-mavi-600 transition-all group"
              title="Yanıtla"
            >
              <MessageCircle className="w-4.5 h-4.5 group-hover:scale-110 transition-transform" />
              {replyCount > 0 && <span className="text-sm font-medium">{replyCount}</span>}
            </button>

            {!isAuthor && (
              <button
                onClick={(e) => { e.stopPropagation(); setIsZapModalOpen(true); }}
                className="p-2 rounded-full hover:bg-orange-500/10 text-gray-500 hover:text-orange-400 transition-all group"
                title="Bitcoin ile Destek Ol"
              >
                <Zap className="w-4.5 h-4.5 group-hover:scale-110 transition-transform" />
              </button>
            )}
          </div>

          <div className="relative" ref={menuRef}>
            <button
              onClick={(e) => { e.stopPropagation(); setIsMenuOpen(!isMenuOpen); }}
              className={`p-2 rounded-full transition-all ${isMenuOpen ? 'bg-surface-100 dark:bg-surface-800 text-mavi-500' : 'text-gray-400 hover:bg-surface-100 dark:hover:bg-surface-800 hover:text-gray-600'}`}
              title="Daha fazla"
            >
              <MoreHorizontal className="w-4.5 h-4.5" />
            </button>

            {isMenuOpen && (
              <div className="absolute right-0 sm:left-0 sm:right-auto bottom-full mb-2 w-48 bg-white/95 dark:bg-surface-900/95 backdrop-blur-md rounded-2xl border border-surface-200/50 dark:border-surface-700/50 shadow-2xl overflow-hidden py-1.5 z-20 animate-in fade-in slide-in-from-bottom-2 duration-200">
                <button
                  onClick={(e) => { e.stopPropagation(); toggleBookmark(); setIsMenuOpen(false); }}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-[13px] font-poppins font-medium transition-colors ${isBookmarked ? 'text-mavi-500 bg-mavi-50/50 dark:bg-mavi-900/10' : 'text-gray-600 dark:text-gray-300 hover:bg-surface-50 dark:hover:bg-surface-800'}`}
                >
                  <Bookmark className={`w-4 h-4 ${isBookmarked ? 'fill-mavi-500' : ''}`} />
                  {isBookmarked ? 'Kaydedildi' : 'Kaydet'}
                </button>

                <button
                  onClick={(e) => { e.stopPropagation(); handleCopyLink(); setIsMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 text-[13px] font-poppins font-medium text-gray-600 dark:text-gray-300 hover:bg-surface-50 dark:hover:bg-surface-800 transition-colors"
                >
                  {copied ? <Check className="w-4 h-4 text-green-500" /> : <LinkIcon className="w-4 h-4" />}
                  {copied ? 'Kopyalandı' : 'Link Kopyala'}
                </button>

                <div className="px-4 py-2.5 border-t border-surface-100 dark:border-surface-800/50">
                  <p className="text-[10px] font-poppins font-bold text-gray-400 dark:text-gray-500 uppercase tracking-[0.1em] mb-2.5">
                    Tepkiler
                  </p>
                  <div className="flex justify-start">
                    <ReactionPicker
                      onSelect={(emoji) => {
                        reactToEvent(entry.id, entry.author_pubkey || '', emoji, sk, entry.topicTitle);
                        setIsMenuOpen(false);
                      }}
                      disabled={!sk && !window.nostr}
                    />
                  </div>
                </div>

                {!isAuthor && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setIsReportModalOpen(true); setIsMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-[13px] font-poppins font-medium text-red-500/90 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors border-t border-surface-100 dark:border-surface-800/50"
                  >
                    <Flag className="w-4 h-4" />
                    Bildir
                  </button>
                )}

                {isAuthor && (
                  <button
                    onClick={(e) => { e.stopPropagation(); setIsEditModalOpen(true); setIsMenuOpen(false); }}
                    className="w-full flex items-center gap-3 px-4 py-2.5 text-[13px] font-poppins font-medium text-mavi-500 hover:bg-mavi-50 dark:hover:bg-mavi-900/10 transition-colors border-t border-surface-100 dark:border-surface-800/50"
                  >
                    <Edit3 className="w-4 h-4" />
                    Düzenle
                  </button>
                )}

                {isAdmin && (
                  <div className="border-t border-surface-100 dark:border-surface-800/50 mt-1 pt-1 bg-surface-50/30 dark:bg-surface-800/20">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setIsMenuOpen(false);
                        if (window.confirm(`${entry.author_username} kullanıcısını sessize almak istediğinize emin misiniz?`)) {
                          muteUser(entry.author_pubkey || entry.author_username)
                        }
                      }}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-[13px] font-poppins font-medium text-orange-500/90 hover:bg-orange-50 dark:hover:bg-orange-900/10 transition-colors"
                    >
                      <UserX className="w-4 h-4" />
                      Yazarı Sessize Al
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setIsMenuOpen(false); handleDelete(); }}
                      disabled={isDeleting}
                      className="w-full flex items-center gap-3 px-4 py-2.5 text-[13px] font-poppins font-medium text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
                    >
                      {isDeleting ? <div className="w-4 h-4 border border-red-500 border-t-transparent rounded-full animate-spin" /> : <Trash2 className="w-4 h-4" />}
                      Entry'yi Kaldır
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Author Metadata (Right) */}
        <div className="flex items-center gap-3 text-right self-end sm:self-auto shrink-0" onClick={(e) => e.stopPropagation()}>
          <div className="flex flex-col items-end">
            <Link
              to={`/profile/${entry.author_pubkey || entry.author_username}`}
              className="font-poppins font-bold text-base text-gray-900 dark:text-gray-100 hover:text-mavi-600 transition-colors truncate max-w-[180px]"
              onClick={(e) => e.stopPropagation()}
            >
              {!isLoading || metadata ? (
                finalUsername
              ) : (
                <div className="h-4 w-16 bg-surface-200 dark:bg-surface-800 rounded animate-pulse" />
              )}
            </Link>
            <span className="text-sm text-gray-400 dark:text-gray-500 font-medium">
              {formatDistanceToNow(new Date(entry.created_at), { addSuffix: true, locale: tr })}
            </span>
          </div>
          <UserAvatar
            src={finalAvatar}
            name={finalUsername}
            size="lg"
            isExternal={entry.isExternal}
          />
        </div>
      </div>

      <EditEntryModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        entry={{
          id: entry.id,
          content: entry.content,
          topicTitle: entry.topicTitle || 'gündem',
          created_at: entry.created_at
        }}
      />
      <ZapModal
        isOpen={isZapModalOpen}
        onClose={() => setIsZapModalOpen(false)}
        targetPubkey={entry.author_pubkey || ''}
        targetName={finalUsername}
        lnAddress={metadata?.lud16}
        eventId={entry.id}
      />
      <ReplyModal
        isOpen={isReplyModalOpen}
        onClose={() => setIsReplyModalOpen(false)}
        replyToEvent={{
          id: entry.id,
          pubkey: entry.author_pubkey || '',
          content: entry.content,
          created_at: Math.floor(new Date(entry.created_at).getTime() / 1000),
          kind: 1,
          tags: [],
          sig: ''
        } as Event}
        authorName={finalUsername}
      />
      <ReportModal
        isOpen={isReportModalOpen}
        onClose={() => setIsReportModalOpen(false)}
        targetId={entry.id}
        targetType="event"
      />

      <Lightbox
        isOpen={!!lightboxState}
        onClose={() => setLightboxState(null)}
        src={lightboxState?.src || null}
        linkTo={lightboxState?.linkTo}
        linkState={lightboxState?.linkState}
        info={lightboxState?.info}
      />
    </motion.div>
  )
}
